/**
 * Search results dropdown for calendar search with infinite scroll
 */

import { useEffect, useRef, useCallback } from 'react'
import { formatDateCzech } from '@/lib/calendar-utils'
import { isCancelledBackground } from '@/lib/constants'
import type { CalendarEvent } from '@/types/calendar'

interface SearchResultsProps {
	results: CalendarEvent[]
	isLoading: boolean
	onSelect: (event: CalendarEvent) => void
	onClose?: () => void
	hasNextPage?: boolean
	isFetchingNextPage?: boolean
	fetchNextPage?: () => void
}

export function SearchResults({
	results,
	isLoading,
	onSelect,
	onClose,
	hasNextPage,
	isFetchingNextPage,
	fetchNextPage,
}: SearchResultsProps) {
	const containerRef = useRef<HTMLDivElement>(null)
	const scrollContainerRef = useRef<HTMLDivElement>(null)
	const loadMoreRef = useRef<HTMLDivElement>(null)

	// Click outside to close
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node) &&
				onClose
			) {
				onClose()
			}
		}

		// Add listener with a slight delay to avoid immediate close on open
		const timeoutId = setTimeout(() => {
			document.addEventListener('mousedown', handleClickOutside)
		}, 100)

		return () => {
			clearTimeout(timeoutId)
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [onClose])

	// Infinite scroll with Intersection Observer
	const handleObserver = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			const [entry] = entries
			if (entry.isIntersecting && hasNextPage && !isFetchingNextPage && fetchNextPage) {
				fetchNextPage()
			}
		},
		[hasNextPage, isFetchingNextPage, fetchNextPage],
	)

	useEffect(() => {
		const element = loadMoreRef.current
		if (!element) return

		const observer = new IntersectionObserver(handleObserver, {
			root: scrollContainerRef.current,
			rootMargin: '100px',
			threshold: 0,
		})

		observer.observe(element)
		return () => observer.disconnect()
	}, [handleObserver])

	// Common container classes for centered dropdown
	const containerClasses = "fixed z-50 left-1/2 -translate-x-1/2 top-20 w-[1020px] rounded-xl border border-[#e8e4df] bg-white shadow-xl"

	if (isLoading) {
		return (
			<div ref={containerRef} className={`${containerClasses} p-4`}>
				<div className="flex items-center justify-center">
					<div className="h-5 w-5 animate-spin rounded-full border-2 border-[#d7ad6c] border-t-transparent" />
					<span className="ml-2 text-sm text-[#8c8275]">
						Vyhledávání...
					</span>
				</div>
			</div>
		)
	}

	if (results.length === 0) {
		return (
			<div ref={containerRef} className={`${containerClasses} p-4`}>
				<p className="text-center text-sm text-[#8c8275]">
					Žádné výsledky nenalezeny
				</p>
			</div>
		)
	}

	return (
		<div ref={containerRef} className={`${containerClasses} overflow-hidden`}>
			<div
				ref={scrollContainerRef}
				className="max-h-[750px] overflow-y-scroll scrollbar-gold"
			>
				{results.map((event) => {
					const { resource } = event
					const isCancelled = isCancelledBackground(resource.background)

					// Type badge colors
					const typeBadgeStyles = {
						ZUB: 'bg-[#f5ebe0] text-[#a88544]',
						DH: 'bg-blue-100 text-blue-700',
						OTHER: 'bg-gray-100 text-gray-700',
					}
					const badgeStyle = typeBadgeStyles[resource.type] || typeBadgeStyles.OTHER

					return (
						<button
							type="button"
							key={event.id}
							onClick={() => onSelect(event)}
							className="flex w-full items-center gap-3 border-b border-[#f0ede8] px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-[#f5ebe0]"
						>
							{/* Color indicator */}
							<div
								className="h-4 w-4 flex-shrink-0 rounded-sm"
								style={{ backgroundColor: resource.background || '#d7ad6c' }}
							/>

							{/* Event info */}
							<div className="min-w-0 flex-1">
								<p className={`truncate font-medium ${isCancelled ? 'text-[#8c8275]' : 'text-[#3d3428]'}`}>
									{event.title}
								</p>
								<p className="text-sm text-[#8c8275]">
									{formatDateCzech(event.start, 'd. MMMM yyyy')},{' '}
									{formatDateCzech(event.start, 'H:mm')} -{' '}
									{formatDateCzech(event.end, 'H:mm')}
								</p>
							</div>

							{/* Type badge */}
							<span className={`flex-shrink-0 rounded-md px-2 py-0.5 text-xs font-medium ${badgeStyle}`}>
								{resource.type}
							</span>
						</button>
					)
				})}

				{/* Load more trigger / Loading indicator */}
				<div ref={loadMoreRef} className="py-3">
					{isFetchingNextPage && (
						<div className="flex items-center justify-center">
							<div className="h-4 w-4 animate-spin rounded-full border-2 border-[#d7ad6c] border-t-transparent" />
							<span className="ml-2 text-sm text-[#8c8275]">
								Načítání dalších...
							</span>
						</div>
					)}
				</div>
			</div>

			{/* Footer with result count and close hint */}
			<div className="border-t border-[#e8e4df] bg-[#faf8f5] px-4 py-2 flex justify-between items-center">
				<p className="text-xs text-[#8c8275]">
					{results.length} výsledků{hasNextPage ? ' (další k dispozici)' : ''}
				</p>
				<p className="text-xs text-[#8c8275]">
					Esc pro zavření • Klikněte pro přechod
				</p>
			</div>
		</div>
	)
}
